import { createTheme, type ThemeOptions } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const espressoThemeOptions: ThemeOptions = {
  name: "espresso",
  palette: {
    mode: "dark",
    primary: { main: rawColor.espresso.main },
    secondary: { main: rawColor.espresso.secondary },
    background: {
      default: rawColor.espresso.background,
      paper: rawColor.espresso.paper,
    },
    text: {
      primary: rawColor.espresso.text.primary,
      secondary: rawColor.espresso.text.secondary,
    },
    action: {
      hover: rawColor.espresso.sidebar.hover,
    },
    error: { main: rawColor.status.error.dark },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.status.active.green },
    divider: rawColor.espresso.divider,
  },
  sidebar: {
    base: colors.sidebar.espresso.base,
    hover: colors.sidebar.espresso.hover,
    selected: colors.sidebar.espresso.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${theme.palette.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.espresso.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.espresso.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.espresso.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.espresso.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.espresso.cell,
          color: rawColor.espresso.text.primary,
        },
        head: {
          backgroundColor: colors.table.espresso.header,
          color: colors.table.espresso.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.espresso.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.espresso.text.primary,
        },
        h6: {
          color: rawColor.espresso.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.espresso.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.espresso.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.espresso.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.espresso.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.espresso.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.espresso.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.espresso.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.espresso.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.espresso.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.espresso.text.secondary,
          "&.Mui-focused": {
            color: rawColor.espresso.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.espresso.text.secondary,
        },
        select: {
          color: rawColor.espresso.text.secondary,
        },
        selectLabel: {
          color: rawColor.espresso.text.secondary,
        },
        selectIcon: {
          color: rawColor.espresso.text.secondary,
        },
        displayedRows: {
          color: rawColor.espresso.text.secondary,
        },
        actions: {
          color: rawColor.espresso.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.espresso.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.espresso.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.espresso.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.espresso.sidebar.selected,
              color: rawColor.espresso.main,
              "&:hover": {
                backgroundColor: rawColor.espresso.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.espresso.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.espresso.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.espresso.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.espresso.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.espresso.paper} !important`,
          borderColor: `${rawColor.espresso.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.espresso.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.espresso.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.espresso.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.espresso.paper} !important`,
          border: `1px solid ${rawColor.espresso.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.espresso.paper} !important`,
          color: `${rawColor.espresso.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.espresso.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.espresso.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.espresso.sidebar.selected} !important`,
            color: `${rawColor.espresso.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.espresso.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.espresso.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.espresso.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.espresso.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.espresso.sidebar.selected} !important`,
              color: `${rawColor.espresso.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.espresso.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.espresso.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.espresso.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.espresso.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.espresso.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.espresso.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.espresso.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.espresso.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.espresso.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.espresso.main} !important`,
          },
        },
      },
    },
  },
};

export const espressoTheme = createTheme(espressoThemeOptions);
